/**
 * Zicabella Theme Fixes
 * Handles: Checkout Navigation, Global Error Logging, Performance Optimizations
 */

(function () {
  'use strict';

  // --- 1. Global Error Logging (Lightweight) ---
  window.onerror = function (message, source, lineno, colno, error) {
    if (Math.random() > 0.1) return; // Sample 10% to avoid flooding
    const logData = {
      url: window.location.href,
      ua: navigator.userAgent,
      message,
      source,
      lineno,
      colno,
      stack: error?.stack
    };
    // Send to a placeholder endpoint or console in dev
    if (window.location.hostname.includes('myshopify.com') || window.location.hostname.includes('localhost')) {
      console.warn('[Client Error]', logData);
    } else {
      // navigator.sendBeacon('/_log-client-error', JSON.stringify(logData)); // Uncomment if endpoint exists
      console.warn('[Client Error]', logData);
    }
  };

  // --- 2. Feature Detection & Helpers ---
  function isIosSafari() {
    const ua = navigator.userAgent;
    const isIOS = /iP(hone|od|ad)/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
    return isIOS && isSafari;
  }

  // --- 3. Checkout Logic (Standard Shopify) ---
  /**
   * Navigate to Shopify's default checkout page.
   * @param {HTMLElement} btn
   */
  function handleCheckout(btn) {
    if (btn.dataset.isProcessing === 'true') return;
    btn.dataset.isProcessing = 'true';
    btn.classList.add('is-processing');
    window.location.href = '/checkout';
  }

  /**
   * Handle the custom Buy Now button checkout redirect.
   * @param {HTMLElement} button
   */
  function triggerShopifyCheckout(button) {
    if (button.dataset.isProcessing === 'true') return;

    // Close the cart drawer if it's open
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }

    const form = button.closest('form');
    if (!form) {
      console.warn('[Checkout] Product form not found.');
      return;
    }

    const variantIdInput = form.querySelector('[name="id"]');
    if (!variantIdInput) {
      console.warn('[Checkout] Variant ID input not found.');
      return;
    }

    const variantId = variantIdInput.value;
    if (!variantId) {
      console.warn('[Checkout] Selected variant ID is empty.');
      return;
    }

    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? quantityInput.value : '1';

    button.disabled = true;
    button.dataset.isProcessing = 'true';
    button.classList.add('is-processing');

    // Add to cart via AJAX first so standard Shopify logic / analytics runs, then redirect
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId),
          quantity: parseInt(quantity)
        }]
      })
    })
    .then(response => {
      window.location.href = '/checkout';
    })
    .catch(error => {
      console.error('[Checkout] AJAX failed, falling back to permalink:', error);
      window.location.href = `/cart/${variantId}:${quantity}`;
    });
  }

  // --- 4. Delegated Event Listeners ---
  document.addEventListener('click', function (e) {
    // 4.1 Checkout Button (Cart Drawer & Page)
    const checkoutBtn = e.target.closest('#cart-drawer-checkout');
    if (checkoutBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleCheckout(checkoutBtn);
      return;
    }

    // 4.2 Custom Buy Now Button Checkout
    const buyNowBtn = e.target.closest('.buy-now-btn');
    if (buyNowBtn) {
      e.preventDefault();
      e.stopPropagation();

      // Check if GoKwik is active/available in the DOM or window
      const gokwikBtn = document.querySelector('.gokwik-buy-now, .gokwik-btn, [data-gokwik-button], .gokwik-buynow-button');
      const hasGoKwik = gokwikBtn || (window.GoKwik && typeof window.GoKwik.buyNow === 'function') || (window.gokwikCheckout && typeof window.gokwikCheckout.init === 'function');

      if (hasGoKwik) {
        if (typeof window.triggerGoKwikCheckout === 'function') {
          window.triggerGoKwikCheckout();
        } else {
          // Close the cart drawer if it's open
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer && typeof cartDrawer.close === 'function') {
            cartDrawer.close();
          }
          if (gokwikBtn) {
            gokwikBtn.click();
          } else if (window.GoKwik && typeof window.GoKwik.buyNow === 'function') {
            window.GoKwik.buyNow();
          } else if (window.gokwikCheckout && typeof window.gokwikCheckout.init === 'function') {
            window.gokwikCheckout.init();
          }
        }
      } else {
        // Fallback to Shopify checkout
        triggerShopifyCheckout(buyNowBtn);
      }
      return;
    }

    // 4.3 Add to Cart (Double-click prevention)
    const atcBtn = e.target.closest('.product-form__submit, .zb-add-to-cart');
    if (atcBtn) {
      if (atcBtn.dataset.isProcessing === 'true') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      atcBtn.dataset.isProcessing = 'true';
      setTimeout(() => { atcBtn.dataset.isProcessing = 'false'; }, 2000);
    }
  }, true);

  // --- 5. Resize Debouncer (Performance) ---
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    document.body.classList.add('is-resizing');
    resizeTimer = setTimeout(() => {
      document.body.classList.remove('is-resizing');
      // Trigger any custom events if needed
    }, 150);
  }, { passive: true });

  // --- 6. Preload Normalization (PERF MATCH) ---
  document.addEventListener("DOMContentLoaded", function () {
    // Fix preloads missing "as" for CSS (common Shopify injected preloads)
    document.querySelectorAll('link[rel="preload"]').forEach(function (el) {
      const href = (el.getAttribute('href') || '').toLowerCase();
      // If it's clearly a css asset or named overflow-list, treat as style
      if (href.endsWith('.css') || href.includes('overflow-list.css')) {
        el.setAttribute('as', 'style');
        // ensure it becomes stylesheet after load
        el.addEventListener('load', function () { el.rel = 'stylesheet'; });
        // if on older browsers, set rel to stylesheet after a short timeout as fallback
        setTimeout(function () {
          if (el.rel !== 'stylesheet') el.rel = 'stylesheet';
        }, 3000);
      }
    });

    // Defer non-critical scripts
    const updateScripts = () => {
      document.querySelectorAll('script[src]').forEach(script => {
        if (script.hasAttribute('data-critical')) return; // keep scripts that are explicitly critical
        // Do not change inline scripts or module scripts intentionally
        const type = script.getAttribute('type') || '';
        if (type.indexOf('module') !== -1) return;
        // If the script is already async/defer, skip
        if (script.hasAttribute('async') || script.hasAttribute('defer')) return;
        // Move to deferred execution
        script.setAttribute('defer', '');
      });
    };
    // Run immediately and also hook into readystatechange/load as backup
    updateScripts();
    document.addEventListener('readystatechange', () => {
      if (document.readyState === 'complete') updateScripts();
    });
    window.addEventListener('load', updateScripts);
  });

})();
